import { type AxiosInstance, type AxiosResponse } from 'axios';
import type { Pfm } from '@aviata/platform-client';

import { FlowType } from '../../shared/config';
import { freedomApp } from '../../entities/freedom-app';
import { freedomDevAuth, freedomProdAuth } from './api';

const getNativeAuthMethod = (app: FlowType) => {
  switch (app) {
    case FlowType.AVIA:
    case FlowType.B2B:
      return freedomApp.lib.avia.authorize;
    case FlowType.RAILWAYS:
      return freedomApp.lib.railways.authorize;
    default:
      return freedomApp.lib.avia.authorize;
  }
};

/**
 * We need to load different auth modules for DEV and PROD env.
 * We also need to import only PROD module for PROD env to prevent unnecessary code in bundle.
 */
export const getAuthMethod = (
  app: FlowType,
  pfm: Pfm,
  isDevAuthEnabled: boolean,
  logonHttp: AxiosInstance,
) => {
  const runEnv = import.meta.env.VITE_RUNTIME_ENV;

  /**
   * We can switch DEV/PROD module for the stage environment and
   * we always need to use DEV module for development
   */
  if (runEnv === 'development' || (runEnv === 'staging' && isDevAuthEnabled)) {
    return freedomDevAuth(pfm.client, logonHttp).auth;
  }

  return freedomProdAuth(pfm.client, getNativeAuthMethod(app)).auth;
};

export const unauthorizedAccessInterceptor = (
  config: AxiosResponse<unknown, unknown> & {
    isUserTokenRequest?: boolean;
    isRefreshTokenRequest?: boolean;
    isConsumerTokenRequest?: boolean;
    isTokenRequest?: boolean;
  },
  userStore: { user: unknown; getUser: () => Promise<unknown> },
) => {
  const {
    isUserTokenRequest,
    isRefreshTokenRequest,
    isConsumerTokenRequest,
    isTokenRequest,
  } = config;

  const isAuthRequest =
    isTokenRequest ||
    isUserTokenRequest ||
    isRefreshTokenRequest ||
    isConsumerTokenRequest;

  if (isAuthRequest || userStore.user) {
    return Promise.resolve(config);
  }

  return userStore.getUser().then(() => config);
};
